import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getOrganization,
  getOrganizationChats,
  readAllChatMessages,
} from "../../api/api";
import { useInboxSubscription, useActiveInboxChat, useUserRealtime } from "../../context/UserRealtimeContext";
import { useMessagingSearch } from "../../context/MessagingSearchContext";
import ChatFrame from "../messaging_page/chat_frame/ChatFrame";
import InboxSearchResults from "../messaging_page/InboxSearchResults";
import { useInboxSearch } from "../messaging_page/useInboxSearch";
import {
  bumpOrgChannelForNewMessage,
  clearChatUnread,
  getSharedMessagePreview,
  shouldIncrementUnread,
  sortOrgChannelsByActivity,
} from "../messaging_page/chatListUtils";
import NewOrgChannelModal from "./NewOrgChannelModal";
import OrgMemberRow from "./OrgMemberRow";
import SignedOutPrompt from "../SignedOutPrompt";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { formatRelativeTimestamp } from "../utils";

/**
 * Insert or replace a chat, then keep org hub ordering (general pinned).
 * @param {Array<object>} list
 * @param {object} chat
 * @returns {Array<object>}
 */
function upsertOrgChat(list, chat) {
  if (!chat?.id) return list || [];
  const id = Number(chat.id);
  const rest = (list || []).filter((c) => Number(c.id) !== id);
  return sortOrgChannelsByActivity([chat, ...rest]);
}

/**
 * Organization channels + members workspace for active affiliates.
 * Route: /organization/:orgId/channels/:chatId?
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
function OrganizationChannelsPage({ userId, user }) {
  const { orgId: orgIdParam, chatId } = useParams();
  const orgId = parseInt(orgIdParam, 10);
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sideView, setSideView] = useState(/** @type {'channels' | 'members'} */ ("channels"));
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const { query: searchQuery, showResults, dismissResults } = useMessagingSearch();
  const inboxSearch = useInboxSearch({
    userId,
    orgId,
    query: searchQuery,
    enabled: showResults,
  });
  const [focusMessageId, setFocusMessageId] = useState(null);
  const [focusKey, setFocusKey] = useState(0);

  const hasChat = Boolean(chatId);
  const routeChatId = chatId ? parseInt(chatId, 10) : null;
  const openChatIdRef = useRef(routeChatId);
  openChatIdRef.current = routeChatId;
  const chatsRef = useRef(chats);
  chatsRef.current = chats;
  const isUserAccount = user?.type === "user";
  const { reportUnreadCleared } = useUserRealtime();
  useActiveInboxChat(routeChatId, orgId);

  const affiliations = useMemo(
    () => (organization?.affiliations || []).filter((a) => a.status === "active"),
    [organization]
  );

  const candidateUsers = useMemo(
    () =>
      affiliations
        .map((a) => a.user)
        .filter((u) => u && Number(u.id) !== Number(userId)),
    [affiliations, userId]
  );

  const refreshChats = useCallback(async () => {
    if (!userId || !orgId || !isUserAccount) return;
    try {
      const chatsRes = await getOrganizationChats(orgId, userId);
      if (chatsRes.success && Array.isArray(chatsRes.data)) {
        setChats(sortOrgChannelsByActivity(chatsRes.data));
      }
    } catch {
      // Keep existing list on transient failures.
    }
  }, [userId, orgId, isUserAccount]);

  const loadData = useCallback(async () => {
    if (!userId || !orgId || !isUserAccount) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [orgRes, chatsRes] = await Promise.all([
        getOrganization(userId, orgId),
        getOrganizationChats(orgId, userId),
      ]);

      if (!orgRes.success || !orgRes.data) {
        setAccessDenied(true);
        setOrganization(null);
        setChats([]);
        return;
      }

      const org = orgRes.data;
      const isAffiliate = (org.affiliations || []).some(
        (a) => Number(a.user_id) === Number(userId) && a.status === "active"
      );
      if (!isAffiliate && org.user_affiliation_status !== "active") {
        setAccessDenied(true);
        setOrganization(org);
        setChats([]);
        return;
      }

      setAccessDenied(false);
      setOrganization(org);
      if (chatsRes.success && Array.isArray(chatsRes.data)) {
        setChats(sortOrgChannelsByActivity(chatsRes.data));
      } else {
        setChats([]);
      }
    } catch (err) {
      console.error(err);
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [userId, orgId, isUserAccount]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!chatId || loading) {
      if (!chatId) setSelectedChat(null);
      return;
    }
    const id = parseInt(chatId, 10);
    const chat = chats.find((c) => Number(c.id) === id);
    if (chat) {
      setSelectedChat((prev) => (prev?.id === chat.id ? { ...prev, ...chat } : chat));
    } else if (!loading && chats.length > 0) {
      navigate(`/organization/${orgId}/channels`, { replace: true });
    }
  }, [chatId, chats, loading, navigate, orgId]);

  const handleMarkChatRead = useCallback(
    (id) => {
      const chat = (chatsRef.current || []).find(
        (c) => Number(c.id) === Number(id)
      );
      const cleared = Number(chat?.unread_count) || 0;
      if (cleared > 0) {
        reportUnreadCleared(cleared, orgId);
      }
      setChats((prev) => clearChatUnread(prev, id));
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(id)
          ? { ...prev, unread_count: 0 }
          : prev
      );
    },
    [reportUnreadCleared, orgId]
  );

  // Clear unread when a channel is open via route.
  useEffect(() => {
    if (!userId || routeChatId == null) return;
    const unread = chats.find((c) => Number(c.id) === routeChatId)?.unread_count;
    if (!(Number(unread) > 0)) return;
    handleMarkChatRead(routeChatId);
    readAllChatMessages(routeChatId, userId).catch(() => {});
  }, [userId, routeChatId, chats, handleMarkChatRead]);

  const filteredChats = useMemo(
    () => sortOrgChannelsByActivity(chats),
    [chats]
  );

  const filteredMembers = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return affiliations;
    return affiliations.filter((a) => {
      const u = a.user || {};
      const hay = `${u.first_name || ""} ${u.last_name || ""} ${u.username || ""} ${a.role || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [affiliations, memberQuery]);

  const updateChatLastMessage = useCallback((id, messageContent, messageTimestamp) => {
    const chatIdNum = parseInt(id, 10);
    setChats((prev) =>
      bumpOrgChannelForNewMessage(prev, chatIdNum, messageContent, messageTimestamp)
    );
    setSelectedChat((prev) => {
      if (prev && prev.id === chatIdNum) {
        return {
          ...prev,
          last_message_content: messageContent,
          last_message_timestamp: messageTimestamp || prev.last_message_timestamp,
          last_message_at: messageTimestamp || prev.last_message_at,
        };
      }
      return prev;
    });
  }, []);

  const handleInboxMessage = useCallback(
    (message) => {
      const chatIdNum = Number(message?.chat_id);
      if (!Number.isFinite(chatIdNum)) return;

      const messageOrgId = Number(message?.org_id) || 0;
      // Ignore personal DMs and other orgs' channels.
      if (messageOrgId !== Number(orgId)) return;

      const known = (chatsRef.current || []).some(
        (c) => Number(c.id) === chatIdNum
      );
      if (!known) {
        refreshChats();
        return;
      }

      const content = getSharedMessagePreview(message);
      const ts = message.created_at || "";
      const increment = shouldIncrementUnread(
        message,
        userId,
        openChatIdRef.current
      );

      setChats((prev) =>
        bumpOrgChannelForNewMessage(prev, chatIdNum, content, ts, {
          incrementUnread: increment,
        })
      );

      setSelectedChat((prev) => {
        if (prev && Number(prev.id) === chatIdNum) {
          return {
            ...prev,
            last_message_content: content,
            last_message_timestamp: ts || prev.last_message_timestamp,
            last_message_at: ts || prev.last_message_at,
          };
        }
        return prev;
      });
    },
    [userId, orgId, refreshChats]
  );

  const handleInboxChatRemoved = useCallback(
    (removedChatId) => {
      const known = (chatsRef.current || []).some(
        (c) => Number(c.id) === Number(removedChatId)
      );
      if (!known) return;
      setChats((prev) =>
        (prev || []).filter((c) => Number(c.id) !== Number(removedChatId))
      );
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(removedChatId) ? null : prev
      );
      if (
        routeChatId != null &&
        Number(routeChatId) === Number(removedChatId)
      ) {
        navigate(`/organization/${orgId}/channels`);
      }
    },
    [navigate, orgId, routeChatId]
  );

  useInboxSubscription({
    onInboxMessage: handleInboxMessage,
    onChatRemoved: handleInboxChatRemoved,
    onReconnect: refreshChats,
  });

  const handleChatCreated = (newChat) => {
    setChats((prev) => upsertOrgChat(prev, newChat));
    setSelectedChat(newChat);
    navigate(`/organization/${orgId}/channels/${newChat.id}`);
  };

  /**
   * Keep channel list + selection in sync when metadata changes (e.g. members added).
   * @param {object} updated
   * @returns {void}
   */
  const handleChatUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    setChats((prev) => upsertOrgChat(prev, updated));
    setSelectedChat((prev) =>
      prev && Number(prev.id) === Number(updated.id) ? { ...prev, ...updated } : prev
    );
  }, []);

  /**
   * Leave the open channel when the current user is removed remotely.
   * @param {number} removedChatId
   * @returns {void}
   */
  const handleRemovedFromChat = useCallback(
    (removedChatId) => {
      if (!removedChatId) return;
      setChats((prev) =>
        prev.filter((c) => Number(c.id) !== Number(removedChatId))
      );
      setSelectedChat((prev) =>
        prev && Number(prev.id) === Number(removedChatId) ? null : prev
      );
      navigate(`/organization/${orgId}/channels`);
      loadData();
    },
    [navigate, orgId, loadData]
  );

  const handleBackToList = useCallback(() => {
    navigate(`/organization/${orgId}/channels`);
  }, [navigate, orgId]);

  /**
   * Open a channel and mark it read.
   * @param {object} chat
   * @returns {Promise<void>}
   */
  const handleChannelClick = useCallback(
    async (chat) => {
      navigate(`/organization/${orgId}/channels/${chat.id}`);
      setFocusMessageId(null);
      dismissResults();
      handleMarkChatRead(chat.id);
      await readAllChatMessages(chat.id, userId);
    },
    [navigate, orgId, handleMarkChatRead, userId, dismissResults]
  );

  const openChannelFromSearch = useCallback(
    (chat, messageId = null) => {
      if (!chat?.id) return;
      if (messageId != null) {
        setFocusMessageId(Number(messageId));
        setFocusKey((k) => k + 1);
        navigate(`/organization/${orgId}/channels/${chat.id}`, {
          state: { focusMessageId: Number(messageId) },
        });
      } else {
        setFocusMessageId(null);
        navigate(`/organization/${orgId}/channels/${chat.id}`);
        handleMarkChatRead(chat.id);
        readAllChatMessages(chat.id, userId).catch(() => {});
      }
      dismissResults();
    },
    [dismissResults, navigate, orgId, handleMarkChatRead, userId]
  );

  const handleSearchChat = useCallback(
    (chat) => {
      openChannelFromSearch(chat);
    },
    [openChannelFromSearch]
  );

  const handleSearchMessage = useCallback(
    (hit) => {
      const existing = chats.find((c) => Number(c.id) === Number(hit.chat_id));
      const chat = existing || {
        id: hit.chat_id,
        name: hit.chat_name,
        chat_type: hit.chat_type,
      };
      openChannelFromSearch(chat, hit.id);
    },
    [chats, openChannelFromSearch]
  );

  const orgChannelOptions = useMemo(
    () => ({
      orgId,
      candidateUsers,
      onChatLeft: (leftId) => {
        setChats((prev) => prev.filter((c) => Number(c.id) !== Number(leftId)));
        navigate(`/organization/${orgId}/channels`);
        loadData();
      },
      onChatDeleted: (deletedId) => {
        setChats((prev) => prev.filter((c) => Number(c.id) !== Number(deletedId)));
        navigate(`/organization/${orgId}/channels`);
      },
    }),
    [orgId, candidateUsers, navigate, loadData]
  );

  const mobilePanelActions = useMemo(
    () => ({
      onBack: handleBackToList,
      backClassName: "md:hidden",
    }),
    [handleBackToList]
  );

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Organization channels"
        description="Sign in to open channels and members for organizations you belong to."
      />
    );
  }

  if (!isUserAccount) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-text-primary">Channels hub</h1>
        <p className="mt-2 text-sm text-text-muted">
          Organization channels are for affiliated user accounts. Manage channels from Organization admin.
        </p>
        <Button asChild className="mt-6">
          <Link to="/organization-admin/channels">Go to Channels admin</Link>
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center text-sm text-text-muted">
        Loading channels…
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-text-primary">Access denied</h1>
        <p className="mt-2 text-sm text-text-muted">
          You need an active affiliation with this organization to open channels.
        </p>
        {organization ? (
          <Button asChild variant="outline" className="mt-6">
            <Link to={`/organization/${orgId}`}>View organization</Link>
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-ui-background">
      <aside
        className={cn(
          "flex w-full flex-col border-ui-border bg-secondary/88 backdrop-blur-md md:w-80 md:shrink-0 md:border-r",
          hasChat || showResults ? "hidden md:flex" : "flex"
        )}
      >
        <div className="space-y-3 border-b border-ui-border p-4">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold text-text-primary">
              {organization?.name || "Organization"}
            </h1>
            <p className="mt-0.5 text-sm text-text-muted">Channels &amp; members</p>
          </div>
          <div className="flex gap-1 rounded-lg bg-ui-background/60 p-1">
            <Button
              type="button"
              size="sm"
              variant={sideView === "channels" ? "default" : "ghost"}
              className="flex-1"
              onClick={() => setSideView("channels")}
            >
              Channels
            </Button>
            <Button
              type="button"
              size="sm"
              variant={sideView === "members" ? "default" : "ghost"}
              className="flex-1"
              onClick={() => setSideView("members")}
            >
              Members
            </Button>
          </div>
          {sideView === "channels" ? (
            <div className="flex justify-end">
              <Button type="button" size="sm" onClick={() => setShowNewChannel(true)}>
                New
              </Button>
            </div>
          ) : (
            <Input
              type="search"
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
              placeholder="Search members…"
              aria-label="Search members"
              className="h-9 bg-ui-card"
            />
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {sideView === "channels" ? (
            filteredChats.length === 0 ? (
              <p className="p-4 text-sm text-text-muted">No channels yet.</p>
            ) : (
              <ul className="flex flex-col gap-0.5 p-2">
                {filteredChats.map((chat) => {
                  const active =
                    selectedChat && Number(selectedChat.id) === Number(chat.id);
                  const unread = Number(chat.unread_count) || 0;
                  const relativeTime = formatRelativeTimestamp(
                    chat.last_message_timestamp || chat.last_message_at
                  );
                  return (
                    <li key={chat.id}>
                      <button
                        type="button"
                        onClick={() => handleChannelClick(chat)}
                        className={cn(
                          "flex w-full flex-col rounded-lg px-3 py-2.5 text-left transition-colors",
                          active
                            ? "bg-nav-active text-accent"
                            : "hover:bg-nav-hover text-text-primary"
                        )}
                      >
                        <span className="flex min-w-0 items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">
                            {chat.is_default ? "# " : ""}
                            {chat.name || "Channel"}
                          </span>
                          {unread > 0 ? (
                            <Badge
                              variant="default"
                              className="shrink-0 px-1.5 py-0 text-[10px] leading-4"
                            >
                              {unread > 99 ? "99+" : unread}
                            </Badge>
                          ) : null}
                        </span>
                        {chat.last_message_content ? (
                          <span className="mt-0.5 flex min-w-0 items-baseline gap-1 text-xs text-text-muted">
                            <span className="min-w-0 flex-1 truncate">
                              {chat.last_message_content}
                            </span>
                            {relativeTime ? (
                              <span className="shrink-0">{relativeTime}</span>
                            ) : null}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )
          ) : (
            <div className="p-2">
              {filteredMembers.length === 0 ? (
                <p className="p-2 text-sm text-text-muted">No members found.</p>
              ) : (
                filteredMembers.map((aff) => (
                  <OrgMemberRow
                    key={aff.id}
                    affiliation={aff}
                    userId={userId}
                    user={user}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </aside>

      <div
        className={cn(
          "min-h-0 min-w-0 flex-1 flex-col",
          hasChat || showResults ? "flex" : "hidden md:flex"
        )}
      >
        {showResults ? (
          <InboxSearchResults
            userId={userId}
            query={searchQuery}
            chats={inboxSearch.chats}
            messages={inboxSearch.messages}
            loading={inboxSearch.loading}
            error={inboxSearch.error}
            onSelectChat={handleSearchChat}
            onSelectMessage={handleSearchMessage}
            onChatRead={handleMarkChatRead}
            onBack={dismissResults}
            chatsLabel="Channels"
            emptyLabel="No channels or messages match your search."
          />
        ) : (
          <ChatFrame
            key={chatId || selectedChat?.id || "empty"}
            userId={userId}
            user={user}
            chat={selectedChat}
            chatId={chatId ? parseInt(chatId, 10) : selectedChat?.id}
            onMessageUpdate={updateChatLastMessage}
            panelActions={mobilePanelActions}
            orgChannelOptions={orgChannelOptions}
            currentUserPfp={user?.pfp_url}
            onChatUpdate={handleChatUpdate}
            onRemovedFromChat={handleRemovedFromChat}
            focusMessageId={focusMessageId}
            focusKey={focusKey}
          />
        )}
      </div>

      {showNewChannel ? (
        <NewOrgChannelModal
          userId={userId}
          orgId={orgId}
          candidateUsers={candidateUsers}
          onClose={() => setShowNewChannel(false)}
          onChatCreated={handleChatCreated}
        />
      ) : null}
    </div>
  );
}

export default OrganizationChannelsPage;
